import type { CSSProperties, ReactNode } from 'react'
import styles from './PageLayout.module.css'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { useScreenHeaderTotalOffset } from '@/components/ScreenHeader'

interface PageProps {
  children?: ReactNode
  testId?: string
}

/**
 * The Page.
 */
export function Page({ children, testId }: PageProps) {
  return (
    <div className={styles.page} data-test={testId}>
      {children}
    </div>
  )
}

interface PageLayoutProps {
  children?: ReactNode
  asideHeader?: ReactNode
  asideBody?: ReactNode
  asideFooter?: ReactNode
  testId?: string
}

/**
 * The PageLayout
 */
export function PageLayout({
  children,
  asideHeader,
  asideBody,
  asideFooter,
  testId,
}: PageLayoutProps) {
  const isSpacious = useIsSpacious()
  const topOffset = useScreenHeaderTotalOffset()

  return (
    <div className={styles.layout} data-test={testId}>
      <div className="max-w-content mx-auto">
        {isSpacious ? (
          <div className={styles.columns}>
            <aside className={styles.secondaryColumn}>
              <div className={styles.asideHeader}>{asideHeader}</div>
              <div className={styles.asideBody}>{asideBody}</div>
              <div className={styles.asideFooter}>{asideFooter}</div>
            </aside>
            <main
              className={styles.primaryColumn}
              style={
                { '--screen-header-top-offset': topOffset } as CSSProperties
              }
            >
              {children}
            </main>
          </div>
        ) : (
          <main>
            {asideHeader}
            {children}
            {asideBody}
            {asideFooter}
          </main>
        )}
      </div>
    </div>
  )
}

export interface DescriptionProps {
  children?: ReactNode
}

export function Description({ children }: DescriptionProps) {
  return <div className={styles.description}>{children}</div>
}

export function Separator() {
  return <div className={styles.separator}></div>
}
